import React, { FC, ReactNode, useEffect, useRef, useState } from "react"
import { KButton, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { LCReleaseForm, Release, SelectOption, User, UserTakedown } from "../../../../../../types"
import { releases, users } from "../../../../../../apis"
import { hashids, mapListToCamelCase } from "../../../../../../utility"
import ErrorModal from "../../modal/ErrorModal"
import Loading from "../../../../../ui/Loading"
import {
  notAllowedProductAttributes,
  notAllowedProductRelationships,
  processCatalogNoString
} from "../../../../../../utility/newReleaseHelpler"
import { useNavigate } from "react-router-dom"
import { appleGenreMapping, errorFilledToastOptions, infoFilledToastOptions } from "../../../../../../constants"
import { toast } from "react-toastify"
import { lang } from "../../../../../../constants/languages"
import { NewReleaseActions, TakedownActionTabs } from "../../../../../../actions/types"
import ConfirmationDialog from "../../../../../ui/modals/ConfirmationDialog"
import { RootState } from "../../../../../../store"
import { connect } from "react-redux"

type TakedownProps = {
  user: User
  setSelectedPage: (selectedPage: number) => void
  selectedTab: number
  setSelectedTab: (value: number) => void
  selectedArtist?: SelectOption
  setSelectedArtist: (selectedArtist?: SelectOption) => void
  selectRelease: (release?: Release, releaseId?: string) => void
  selectedAlbumIds?: string[]
  selectedUserTakedown?: UserTakedown
  onFinishTakedown?: () => void
}

// Spotify Track
type Track = {
  id: string
  name: string
  isrc: string
  artists_list: string[]
  track_number: number
  explicit: boolean
}

type Artist = {
  name: string
}

// Spotify Album
type Album = {
  id: string
  name: string
  cover: string
  releaseDate: string
  totalTracks: number
  artistsList: Artist[]
}

const Takedown: FC<TakedownProps> = (props) => {
  const { user, setSelectedPage, selectedTab, setSelectedTab, selectedArtist, setSelectedArtist, selectRelease } = props

  const [errorText, setErrorText] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<SelectOption[]>([])
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>()
  const [searchAlbumText, setSearchAlbumText] = useState("")
  const [selectedAlbums, setSelectedAlbums] = useState<Album[]>([])
  const [albums, setAlbums] = useState<Album[]>([])
  const [hasNext, setHasNext] = useState()
  const [offset, setOffset] = useState(0)
  const [isUpload, setIsUpload] = useState(false)
  const [confirmDialogOpen, setConfirmedDialogOpen] = useState(false)

  const canvasRef = useRef<HTMLCanvasElement>(null)

  const navigate = useNavigate()

  const searchParams = new URLSearchParams(location.search)

  const fileInputRef = useRef(null)

  useEffect(() => {
    if (searchParams && searchParams.get("upload")) {
      setIsUpload(true)
    }
  }, [searchParams])

  useEffect(() => {
    if (props.selectedAlbumIds) {
      return
    }
    if (user.artistSpotifyId) {
      onSearch(user.artistSpotifyId, true)
    }

    props.setSelectedArtist(undefined)
    setSelectedTab(TakedownActionTabs.SPOTIFY_ARTIST)
  }, [])

  const fetchAlbums = (
    artistId: string,
    offset: number,
    albums: Album[],
    spAlbumIds?: string[],
    selectAlbums?: boolean
  ) => {
    const albumSpIdParam = spAlbumIds ? `&sp_album_ids=${spAlbumIds.join(",")}` : ""
    setLoading(true)
    users(`/artist_albums_spotify/?artist_id=${artistId}&offset=${offset}${albumSpIdParam}`, "GET").then((resp) => {
      setLoading(false)
      const results = mapListToCamelCase(resp.data.results)
      setAlbums([...albums, ...results])
      setHasNext(resp.data.next)
      if (selectAlbums) {
        setSelectedAlbums([...results])
      }
    })
  }

  useEffect(() => {
    if (!props.selectedAlbumIds) {
      return
    }
    fetchAlbums(user.artistSpotifyId, 0, [], props.selectedAlbumIds, true)
  }, [props.selectedAlbumIds])

  const onSearch = (artistSpotifyId: string, retry: boolean) => {
    if (!artistSpotifyId) {
      setLoading(false)
      return
    }

    setLoading(true)
    users(`/search_artist_spotify/?spotify_id=${artistSpotifyId}&search=`, "GET").then((resp) => {
      setLoading(false)
      setOptions(resp.data.results)
    })
  }

  /*const handleInputChange = (text: string) => {
    setSearchArtistText(text)
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    const newTimeoutId = setTimeout(() => {
      // Call onSearch after 1 second
      onSearch(text, true)
    }, 1000)

    setTimeoutId(newTimeoutId)
  }*/

  const onClickArtist = (option: SelectOption) => {
    setSelectedArtist(option)
    setOffset(0)
    if (!isUpload) {
      fetchAlbums(option.value.toString(), 0, [])
    }

    setSelectedTab(TakedownActionTabs.SELECT_ALBUM)
  }

  const onLoadMore = () => {
    if (!selectedArtist) {
      return
    }
    setOffset(offset + 50)
    fetchAlbums(selectedArtist.value.toString(), offset + 50, albums)
  }

  const handleFileChange = async (event: any) => {
    setLoading(true)
    const file = event.target.files[0]
    if (!file || !selectedArtist) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string // Explicitly assert as string
      if (!text) return

      const rows = text.split("\n").map((row) => row.trim())

      if (rows.length < 2) return

      // Extract headers and find the index of "track_album_albumId"
      const headers = rows[0].split(",")
      const albumIdIndex = headers.indexOf("track_album_albumId")

      if (albumIdIndex === -1) {
        toast.error("Column 'track_album_albumId' not found.")
        return
      }

      // Extract and filter unique album IDs
      const albumIds = new Set<string>()
      for (let i = 1; i < rows.length; i++) {
        const columns = rows[i].split(",")
        if (columns[albumIdIndex]) {
          albumIds.add(columns[albumIdIndex].trim())
        }
      }

      fetchAlbums(selectedArtist.value.toString(), 0, [], [...albumIds])

      setLoading(false)
    }

    reader.readAsText(file)
  }

  const renderSearchResults = () => {
    return (
      <div className="render-search-results-div">
        <div className="mt-6 pb-8" style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.20)" }}>
          <div className="block">
            {options.length > 0 &&
              options.map((option) => {
                return (
                  <div
                    data-testid="artist-option"
                    className="flex mb-3"
                    style={{ cursor: "pointer", backgroundColor: "#F5F5F5", borderRadius: 10 }}
                    key={`${option.value}${option.type}`}
                    onClick={() => onClickArtist(option)}
                  >
                    <img
                      className="my-2.5 ml-3.5 mr-4"
                      style={{ width: 36, height: 36, borderRadius: 50 }}
                      src={option.cover || "/account_icons/avatar.svg"}
                    />
                    <div className={"grid items-center"}>
                      <KSpan
                        text={option.label?.toString() || ""}
                        color="#000"
                        fontSize={16}
                        fontWeight={500}
                        lineHeight="24px"
                        letterSpacing="-0.176px"
                      />
                    </div>
                  </div>
                )
              })}
            {options.length === 0 && (
              <React.Fragment>
                <div className="flex flex-row justify-between items-center gap-2.5 mt-6">
                  <hr style={{ flexGrow: 1 }} />
                  <KSpan
                    text={lang.common.or.toUpperCase()}
                    fontWeight={500}
                    fontSize={11}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                    color="#111"
                  />
                  <hr style={{ flexGrow: 1 }} />
                </div>
                <div
                  style={{
                    height: 196,
                    backgroundColor: "#F5F5F5",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 24,
                    padding: 24,
                    alignContent: "center",
                    borderRadius: "10px",
                    marginTop: "24px"
                  }}
                >
                  <img src={"/account_icons/music-album.svg"} />
                  <div className="flex flex-col gap-3 justify-center items-center w-full">
                    <KSpan text={lang.catalog.new_release.no_results} />
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderSearchBox = () => {
    return (
      <div className="render-search-box">
        {/*
          <div>
          <KInput
            value={searchArtistText}
            onChange={(value) => handleInputChange(value)}
            placeholder={lang.catalog.new_release.artist_name_placeholder}
            leftIcon={"/search.svg"}
            padding="14px"
            gap="8px"
          />
        </div>
      */}
        {user.artistSpotifyId && (
          <div>
            {loading ? (
              <div
                style={{
                  height: 196,
                  backgroundColor: "#F7F7F7",
                  textAlign: "center",
                  justifyItems: "center",
                  gap: 24,
                  padding: 24,
                  alignContent: "center",
                  borderRadius: "10px",
                  marginTop: 24
                }}
                className={"grid"}
              >
                <img src={"/account_icons/music-album.svg"} alt="music-album" />
                <div className="flex flex-row">
                  <KSpan text={lang.catalog.new_release.searching_in_database} />
                  <span className="dots"></span>
                </div>
              </div>
            ) : (
              renderSearchResults()
            )}
          </div>
        )}
      </div>
    )
  }

  const sleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  const saveProductAndTracks = (
    releaseForm: LCReleaseForm,
    labelName: string,
    takedownData: any,
    isMulti: boolean
  ): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      const productRelationships: Record<string, any> = {}
      Object.entries(releaseForm.product.data.relationships).forEach(([key, value]) => {
        if (value["data"] && !notAllowedProductRelationships.includes(key)) {
          productRelationships[key] = { data: value["data"] }
        }
      })

      const productAttributes: Record<string, any> = {}
      Object.entries(releaseForm.product.data.attributes).forEach(([key, value]) => {
        if (value && !notAllowedProductAttributes.includes(key)) productAttributes[key] = value
      })

      const labelId = releaseForm.product.data.relationships.label?.data?.id
      const labelInternalReference = processCatalogNoString(productAttributes["name"], labelId)

      const filteredReleaseForm = {
        label_name: labelName,
        product: {
          data: {
            id: releaseForm.product.data.id,
            attributes: {
              ...productAttributes,
              "label-internal-reference": labelInternalReference,
              "is-compilation": false
            },
            relationships: productRelationships,
            type: "products"
          }
        },
        is_urgent: false,
        takedown: true
      }

      const errorMessages: string[] = []

      try {
        toast.info(lang.catalog.new_release.catalog_takedown_started, infoFilledToastOptions)
        const userIdParam = props.selectedUserTakedown
          ? `?user_takedown_user_id=${props.selectedUserTakedown.userId}`
          : ""
        const productResponse = await releases(
          `/release/label_camp_product_save/${userIdParam}`,
          "post",
          filteredReleaseForm
        )

        if (!productResponse.data.success) {
          errorMessages.push(
            `${lang.catalog.new_release.error.product_response}: ${JSON.stringify(productResponse.data.result)}`
          )
        }

        if (productResponse.data.release_id && productResponse.data.result.product.data.id) {
          const productId = productResponse.data.result.product.data.id

          const labelUpdateRequest = {
            c_label: takedownData["c_label"],
            p_label: takedownData["p_label"],
            product_id: productId
          }
          await sleep(500)
          await releases(`/release/label_camp_product_update_labels/`, "put", labelUpdateRequest)

          const trackList = takedownData["track_list"]
          for (const track of trackList) {
            try {
              const trackAttributes: any = {
                "volume-number": 1,
                "track-number": track["track_number"],
                title: track["name"],
                "isrc-code": track["isrc"],
                artist: track["artists_list"],
                "parental-advisory": track["explicit"] || false
              }

              if (takedownData["track_credits_map"] && takedownData["track_credits_map"][track["isrc"]]) {
                let composer = []
                let lyricist = []
                const creditsMap = takedownData["track_credits_map"][track["isrc"]]
                if (creditsMap["Producer"] && creditsMap["Producer"].length > 0) {
                  trackAttributes["producer"] = creditsMap["Producer"]
                }
                if (creditsMap["Performer"] && creditsMap["Performer"].length > 0) {
                  trackAttributes["performer"] = creditsMap["Performer"]
                }
                if (creditsMap["Arranger"] && creditsMap["Arranger"].length > 0) {
                  trackAttributes["arranger"] = creditsMap["Arranger"]
                }
                if (creditsMap["Composer"] && creditsMap["Composer"].length > 0) {
                  composer = creditsMap["Composer"]
                }
                if (creditsMap["Lyrics"] && creditsMap["Lyrics"].length > 0) {
                  lyricist = creditsMap["Lyrics"]
                }
                if (creditsMap["Songwriter"] && creditsMap["Songwriter"].length > 0) {
                  composer = [...new Set([...composer, ...creditsMap["Songwriter"]])]
                  lyricist = [...new Set([...lyricist, ...creditsMap["Songwriter"]])]
                }

                if (composer.length > 0) {
                  trackAttributes["composer"] = composer
                }
                if (lyricist.length > 0) {
                  trackAttributes["lyricist"] = lyricist
                }
              }

              const requestBody = {
                track: {
                  data: {
                    relationships: {
                      product: { data: { id: productId, type: "products" } }
                    },
                    attributes: trackAttributes,
                    type: "tracks"
                  }
                }
              }

              setLoading(true)
              const trackResponse = await releases(`/release/label_camp_track_create/`, "post", requestBody)

              if (!trackResponse.data.success) {
                errorMessages.push(
                  `${lang.catalog.new_release.error.track_response}: ${JSON.stringify(trackResponse.data.result)}`
                )
              }
            } catch (err) {
              errorMessages.push(`${lang.catalog.new_release.error.track_error}: ${err}`)
            }
          }

          let useCredit = false
          const productTitle = releaseForm.product.data.attributes["name"]
          if (isMulti) {
            if (productResponse.data.success) {
              useCredit = true
              toast.success(`Takedown successful for album: ${productTitle}`)
            } else {
              toast.error(`Failed to takedown album: ${productTitle}`, errorFilledToastOptions)
            }
          } else {
            if (productResponse.data.release_id) {
              useCredit = true
            }
          }

          if (useCredit && !props.selectedUserTakedown) {
            try {
              const resp = await users("/use_takedown_credit/", "PUT", {})
              if (resp.data.success) {
                if (!isMulti) {
                  setLoading(true)
                  selectRelease(undefined, productResponse.data.release_id?.toString())
                  setTimeout(() => {
                    setLoading(false)
                    navigate(
                      `/catalog/asset-registry/${hashids.encode(productResponse.data.release_id?.toString() || "")}`
                    )
                  }, 500)
                }
              } else {
                setErrorText(lang.catalog.new_release.error.no_credit)
              }
            } catch (err) {
              errorMessages.push(`Error using takedown credit: ${err}`)
            }
          }
        }
      } catch (e: any) {
        console.error(lang.catalog.new_release.error.error_on_saving_product)
        errorMessages.push(e)
      } finally {
        setLoading(false)
        if (errorMessages.length > 0) {
          setErrorText(errorMessages.join("\n\n"))
          toast.error(errorMessages.join(" ---- "), errorFilledToastOptions)
        }
        resolve()
      }
    })
  }

  const onSelectAlbumForTakedown = async (album: Album, isMulti: boolean) => {
    return new Promise<void>((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = "anonymous" // Allow cross-origin images
      img.src = album.cover

      img.onload = async () => {
        try {
          const canvas = canvasRef.current

          if (canvas) {
            const ctx = canvas.getContext("2d") as CanvasRenderingContext2D
            canvas.width = 3000
            canvas.height = 3000

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
            const resizedImage = canvas.toDataURL("image/jpeg")

            setLoading(true)
            const resp = await users(`/artist_album_tracks_spotify/?album_id=${album.id}`, "GET")

            if (resp.data.results.length === 0) {
              setErrorText(lang.catalog.new_release.error.error_on_finding_result)
              setLoading(false)
              return reject("No results found")
            }

            const userIdParam = props.selectedUserTakedown
              ? `?force_upload_user_id=${props.selectedUserTakedown.userId}`
              : ""
            const takedownData = resp.data.results
            const optionResponse = await releases(`/release/label_camp_options/${userIdParam}`, "get")
            const labelOptions = optionResponse.data["label_options"]

            const productTypeRelationship = {
              "product-type": { data: { type: "product-types", id: takedownData["total_tracks"] === 1 ? "2" : "1" } }
            }

            const productDataRelationships =
              labelOptions?.length >= 1
                ? { ...productTypeRelationship, label: { data: { id: labelOptions[0].value, type: "labels" } } }
                : { ...productTypeRelationship }

            const labelName = labelOptions[0].label
            const productionYear = takedownData["release_date"].substring(0, 4)
            const label = takedownData["label"].trim()

            const productAttributes: any = {
              name: takedownData["name"],
              "upc-code": takedownData["upc"],
              "original-release-date": `${takedownData["release_date"]}T09:00:00.000Z`,
              "production-year": productionYear,
              "copyright-line": `${productionYear} ${label}`,
              "production-line": `${productionYear} ${label}`,
              artist: takedownData["artists_list"],
              "front-cover": resizedImage
            }
            if (takedownData["total_credits_map"]) {
              let composer = []
              let lyricist = []
              const creditsMap = takedownData["total_credits_map"]
              if (creditsMap["Producer"] && creditsMap["Producer"].length > 0) {
                productAttributes["producer"] = creditsMap["Producer"]
              }
              if (creditsMap["Performer"] && creditsMap["Performer"].length > 0) {
                productAttributes["performer"] = creditsMap["Performer"]
              }
              if (creditsMap["Arranger"] && creditsMap["Arranger"].length > 0) {
                productAttributes["arranger"] = creditsMap["Arranger"]
              }
              if (creditsMap["Composer"] && creditsMap["Composer"].length > 0) {
                composer = creditsMap["Composer"]
              }
              if (creditsMap["Lyrics"] && creditsMap["Lyrics"].length > 0) {
                lyricist = creditsMap["Lyrics"]
              }
              if (creditsMap["Songwriter"] && creditsMap["Songwriter"].length > 0) {
                composer = [...new Set([...composer, ...creditsMap["Songwriter"]])]
                lyricist = [...new Set([...lyricist, ...creditsMap["Songwriter"]])]
              }

              if (composer.length > 0) {
                productAttributes["composer"] = composer
              }
              if (lyricist.length > 0) {
                productAttributes["lyricist"] = lyricist
              }
            }

            const releaseForm: LCReleaseForm = {
              product: {
                data: {
                  id: undefined,
                  attributes: productAttributes,
                  relationships: productDataRelationships,
                  type: "products"
                }
              },
              offer: { data: { id: undefined, attributes: {}, relationships: { product: {} }, type: "offers" } },
              tracks: [],
              import_tasks: [],
              import_sub_tasks: [],
              records: [],
              track_offers: [],
              logs: []
            }

            await saveProductAndTracks(releaseForm, labelName, takedownData, isMulti)
            resolve()
          }
        } catch (err: any) {
          setErrorText(err)
          setLoading(false)
          reject(err)
        }
      }

      img.onerror = (err) => {
        reject(err)
      }
    })
  }

  const onTakedownAll = async () => {
    for (let i = 0; i < albums.length; i++) {
      const userCreditRepsonse = await users(`/fetch_user_credit/`, "GET")
      if (userCreditRepsonse.data.result.takedown > 0) {
        await onSelectAlbumForTakedown(albums[i], true)
      } else {
        setErrorText(lang.catalog.new_release.error.no_credit)
        toast.error(lang.catalog.new_release.error.no_credit, errorFilledToastOptions)
      }
    }

    setTimeout(() => {
      navigate(`/catalog/`)
    }, 250)
  }

  const handleConfirmAlbumSelect = async () => {
    if (selectedAlbums.length === 0) {
      return
    }

    const isMulti = selectedAlbums.length !== 1
    for (let i = 0; i < selectedAlbums.length; i++) {
      const progress = Math.round(((i + 1) / selectedAlbums.length) * 100)
      const userTakedownStatus = props.selectedUserTakedown
        ? {
            user_takedown_id: props.selectedUserTakedown?.id,
            user_id: props.selectedUserTakedown?.userId,
            status: progress === 100 ? "success" : "in_progress",
            progress
          }
        : undefined
      const userCreditRepsonse = await users(`/fetch_user_credit/`, "GET")
      if (userCreditRepsonse.data.result.takedown > 0) {
        await onSelectAlbumForTakedown(selectedAlbums[i], isMulti)
        if (userTakedownStatus) {
          await users(`/save_user_takedown_metadata_status/`, "PUT", { ...userTakedownStatus })
        }
      } else {
        if (userTakedownStatus) {
          userTakedownStatus.status = "failed"
          await users(`/save_user_takedown_metadata_status/`, "PUT", { ...userTakedownStatus })
        }
        setErrorText(lang.catalog.new_release.error.no_credit)
        toast.error(lang.catalog.new_release.error.no_credit, errorFilledToastOptions)
        break
      }
    }

    if (isMulti && !props.selectedUserTakedown) {
      setTimeout(() => {
        navigate(`/catalog/`)
      }, 250)
    }
    if (props.selectedUserTakedown && props.onFinishTakedown) {
      props.onFinishTakedown()
    }
  }

  const onSelectAlbum = (album: Album) => {
    if (selectedAlbums?.includes(album)) {
      setSelectedAlbums(selectedAlbums.filter((selected) => selected.id !== album.id))
    } else {
      setSelectedAlbums([...selectedAlbums, album])
    }
  }

  const renderAlbums = () => {
    return albums
      .filter((album) => album.name.toLowerCase().includes(searchAlbumText.toLowerCase()))
      .map((album) => {
        const songLabel =
          album.totalTracks > 1 ? `${album.totalTracks} ${lang.common.songs_uppercase}` : lang.common.single_uppercase
        return (
          <div
            data-testid="album-option"
            className={`takedown-album-div cursor-pointer`}
            style={selectedAlbums.includes(album) ? { background: "#dddddd" } : {}}
            key={album.id}
            onClick={() => onSelectAlbum(album)}
          >
            <div className="flex gap-4">
              <img src={album.cover} style={{ width: 40, height: 40, borderRadius: 6 }} />
              <KTitleSpan text={album.name} fontSize={20} lineHeight="28px" fontWeight={500} />
            </div>
            <div className="flex gap-2" style={{ alignItems: "center" }}>
              <div className="takedown-album-pill-div">
                <KSpan text={selectedArtist?.label?.toString() || "-"} fontSize={11} color="#000" />
              </div>
              <div className="takedown-album-pill-div">
                <KSpan text={album?.releaseDate || "-"} fontSize={11} color="#000" />
              </div>
              <div className="takedown-album-pill-div">
                <KSpan text={songLabel} fontSize={11} color="#000" />
              </div>
            </div>
          </div>
        )
      })
  }

  const renderArtistSelect = () => {
    if (!user.artistSpotifyId) {
      return (
        <div>
          <KSpan text={lang.catalog.new_release.error.no_spotify_id} />
        </div>
      )
    }

    return (
      <div>
        <div style={{ marginTop: 12 }}>
          <KSpan text={lang.catalog.new_release.spotify_artist_selection_desc} />
        </div>
        <div className={"mt-6"}>{renderSearchBox()}</div>
      </div>
    )
  }

  const renderAlbumSelect = () => {
    if (!selectedArtist) {
      return <div />
    }

    return (
      <div>
        {isUpload && (
          <div className="flex justify-between mt-4">
            <KTitleSpan fontSize={16} lineHeight="20px" text="Upload Csv File exported from Scouting Confirmed Offer" />
            <KButton
              width="200px"
              textColor="black"
              background="transparent"
              text="+Upload File"
              //@ts-ignore
              onClick={() => fileInputRef?.current && fileInputRef.current.click()}
            />
            <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
          </div>
        )}
        <div style={{ marginTop: 24 }}>
          <KInput
            value={searchAlbumText}
            onChange={(value) => setSearchAlbumText(value)}
            placeholder={lang.catalog.new_release.album_name_placeholder}
            leftIcon={"/search.svg"}
            padding="14px"
            gap="8px"
          />
        </div>
        <div style={{ marginTop: 24 }}>
          <KButton
            disabled={selectedAlbums.length === 0}
            background="black"
            text={lang.catalog.new_release.takedown_albums}
            onClick={() => setConfirmedDialogOpen(true)}
            textColor="white"
          />
        </div>
        <div className={"mt-6 mb-6 pb-2"} style={{ border: "1px solid #F3F3F3", borderRadius: 10 }}>
          {renderAlbums()}
        </div>
        {hasNext && (
          <div className={"pb-6 flex justify-center"}>
            <KButton
              text={lang.catalog.new_release.load_more}
              width="160px"
              background="white"
              onClick={() => onLoadMore()}
              disabled={loading}
            />
          </div>
        )}
        {isUpload && albums?.length > 0 && (
          <div className={"pb-6 flex justify-center"}>
            <KButton
              text={lang.catalog.new_release.takedown_all}
              width="160px"
              background="white"
              onClick={() => onTakedownAll()}
              disabled={loading}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex gap-6">
      {loading && <Loading />}
      <ErrorModal text={errorText} setText={setErrorText} />
      {!props.selectedAlbumIds && (
        <div className="cursor-pointer mt-3" onClick={() => setSelectedPage(NewReleaseActions.FINISH_RELEASE)}>
          <img src="/analytics_icons/caret-left.svg" style={{ borderRadius: 10, padding: 8, background: "#F7F7F7" }} />
        </div>
      )}
      <div>
        <div>
          <KTitleSpan text={lang.catalog.new_release.move_album} fontSize={32} />
        </div>
        {selectedTab === TakedownActionTabs.SPOTIFY_ARTIST && renderArtistSelect()}
        {selectedTab === TakedownActionTabs.SELECT_ALBUM && renderAlbumSelect()}
      </div>
      <ConfirmationDialog
        openConfirmation={confirmDialogOpen}
        setOpenConfirmation={setConfirmedDialogOpen}
        handleConfirm={handleConfirmAlbumSelect}
        loading={loading}
        overrideDescription={lang.catalog.new_release.takedown_confirmation_desc.replace(
          "<param>",
          selectedAlbums.map((selectedAlbum) => selectedAlbum.name).join(" ||| ")
        )}
      />
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  )
}

export default Takedown
